// Imports
import { Add, ArrowBack, Cancel, LocalOffer } from "@mui/icons-material";
import { Button, Checkbox, Chip, CircularProgress, Dialog, DialogContent, Drawer, InputAdornment, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import { CSSProperties, Fragment, useEffect, useRef, useState } from "react";
import { GobikesApi } from "../../config/Apis";
import { Colors, DEFAULT_LOADER_STYLES } from "../../config/Constants";
import { getAuthCookie } from "../auth/AuthUtil";
import { betweenValuesValidationFn, greaterThanValueValidationFn, lessThanValueValidationFn, nonNegativeValidationFn } from "../utils/ValidationUtils";
import { GbkFieldType, GobikesField } from "../utils/GobikesField";
import { DashboardLoader } from "../utils/LocalStateUtils";
import { BikeModel, HubBikeModel, OneBike, BikePrice, HubBikeUploadLabels, HubBikePerQuantityImageUploads, HubBikePerQuantityUploadLabels, HubBikeOverrideModel, UploadModel, VehicleType, KmPackage, HubBikeDiscountType } from "../utils/Models";
import { getHubSelection } from "../../features/hubSelection";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";

// Constants and interfaces
interface LocalState {
  loading?: boolean;
  error?: boolean;
  message?: string;
  bikeClassData?: BikeModel[]
  bikeClassMap?: { [k: number]: BikeModel },
  resetError?: () => any
}
type HubBikeImageUploads = { [k in HubBikeUploadLabels]?: string }

// Individual booking details
export function BikeDetails(props: {
  hubBike: HubBikeModel,
  backFn: (reload: boolean) => void,
  drawerWidth: number,
  editMode: boolean,
  creating: boolean,
  initialAction?: 'discount' | 'photos'
}) {

  // Local states
  const [editMode, setEditMode] = useState(props.editMode);
  const photoSectionRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<LocalState>({});
  const [currentBikeData, setCurrentBikeData] = useState<{
    hubBike: HubBikeModel,
    bike: BikeModel,
    upload: HubBikeImageUploads,
    overrides: HubBikeOverrideModel[]
    deletedOverrides: Set<number>,
  }>({
    hubBike: {
      ...props.hubBike,
      bikes: props.hubBike.bikes.map(a => ({ ...a, newUploads: {} as HubBikePerQuantityImageUploads })),
      availability: { ...props.hubBike.availability },
      penalty: props.hubBike.penalty || {}
    },
    bike: JSON.parse(JSON.stringify(props.hubBike.bike)),
    upload: {},
    overrides: JSON.parse(JSON.stringify(props.hubBike.hub_bikes_overrides || [])),
    deletedOverrides: new Set([])
  });

  const [vehicleUploads, setVehicleUploads] = useState<{ [label: string]: UploadModel }>({});
  const [uploadingLabel, setUploadingLabel] = useState<string | null>(null);
  const [discountModal, setDiscountModal] = useState<{ open: boolean, discountType?: string, value?: number, maxValue?: number }>({ open: false });
  const [photoDisplayValues, setPhotoDisplayValues] = useState<{ [label: string]: string }>(() => {
    const v: { [label: string]: string } = {};
    for (const label of Object.values(HubBikeUploadLabels)) {
      const url = props.hubBike?.imageData?.[label as HubBikeUploadLabels];
      if (url) v[label] = url;
    }
    return v;
  });

  // Globals and constants
  const authCookie = getAuthCookie();
  const selectedHub = useSelector(getHubSelection);
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up('md'));

  // Load data on when component opens
  useEffect(() => {
    if (state.bikeClassData) return;
    setState({ ...state, loading: true });
    (async () => {
      try {
        const { data } = await GobikesApi.get(`/bikes/classes`, {
          headers: { 'vendor-token': authCookie },
          params: { notInHub: selectedHub?.id }
        });
        if (!data.data) {
          throw new Error('Failed to load bike class data!');
        }
        setState({ ...state, loading: false, bikeClassData: data.data, bikeClassMap: data.data.reduce((a: { [k: number]: BikeModel }, v: BikeModel) => ({ ...a, [v.id]: v }), {}) });
      } catch (e: any) {
        setState({ ...state, loading: false, error: true, message: e.message });
      }
    })();
  }, [props]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load vendor-uploaded images (pending + approved) for this bike — separate effect so it always runs on mount
  useEffect(() => {
    if (!props.hubBike?.id || !props.hubBike?.hubId || !props.hubBike?.bikeId) return;
    (async () => {
      try {
        const { data } = await GobikesApi.get(`/vendors/hubs/${props.hubBike.hubId}/bikes`, {
          headers: { 'vendor-token': authCookie },
          params: { bikeId: props.hubBike.bikeId, uploads: true }
        });
        const allHubBikes: any[] = data?.data || [];
        const thisHubBike = allHubBikes.find((hb: any) => hb.id === props.hubBike.id) || allHubBikes[0];
        const uploads: UploadModel[] = thisHubBike?.Uploads || thisHubBike?.uploads || [];
        const uploadByLabel: { [label: string]: UploadModel } = {};
        for (const upload of uploads) {
          const isVendorUpload = upload.label && upload.ownerType === 'VENDOR';
          const isNewerThanExisting = !uploadByLabel[upload.label!] || upload.id > uploadByLabel[upload.label!].id;
          if (isVendorUpload && isNewerThanExisting) {
            uploadByLabel[upload.label!] = upload;
          }
        }
        setVehicleUploads(uploadByLabel);
        setPhotoDisplayValues(prev => {
          const next = { ...prev };
          for (const [label, upload] of Object.entries(uploadByLabel)) {
            if (upload.publicUrl) next[label] = upload.publicUrl;
          }
          return next;
        });
      } catch (_) { /* non-critical */ }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle deep-linked action (e.g. jumping here from the Analytics "Underperforming Vehicles" section)
  // Depends on props.initialAction (not mount-only) since BikeDashboard sets it via a separate,
  // un-batched setState in React 17 that lands a render after the one that mounts this component.
  useEffect(() => {
    if (props.initialAction === 'discount') {
      setEditMode(true);
      openDiscountModal();
    } else if (props.initialAction === 'photos') {
      setTimeout(() => photoSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    }
  }, [props.initialAction]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset current bike data when edit mode changed
  useEffect(() => {
    if (!editMode) setCurrentBikeData({
      hubBike: {
        ...props.hubBike,
        bikes: props.hubBike.bikes.map(a => ({ ...a, newUploads: {} as HubBikePerQuantityImageUploads })),
        availability: { ...props.hubBike.availability },
        needDeposit: props.hubBike.deposit > 0 ? 1 : 0,
        needWeekendPrice: Number(props.hubBike.prices.weekEnd) > 0 ? 1 : 0
      },
      bike: JSON.parse(JSON.stringify(props.hubBike.bike)),
      upload: {},
      overrides: JSON.parse(JSON.stringify(props.hubBike.hub_bikes_overrides || [])),
      deletedOverrides: new Set([])
    });
  }, [editMode, props]);

  // Function to update current bike state
  function updateCurrentBikeData(update: Partial<BikeModel>) {
    setCurrentBikeData({ ...currentBikeData, bike: { ...currentBikeData.bike, ...update } })
  }
  function updateCurrentHubBikeData(update: Partial<HubBikeModel>) {
    setCurrentBikeData({ ...currentBikeData, hubBike: { ...currentBikeData.hubBike, ...update } })
  }
  async function handleVehicleImageUpload(label: HubBikeUploadLabels, base64: string | undefined) {
    if (props.creating) {
      setCurrentBikeData(prev => {
        const upload = { ...prev.upload };
        if (base64) upload[label] = base64;
        else delete upload[label];
        return { ...prev, upload };
      });
      setPhotoDisplayValues(prev => ({ ...prev, [label]: base64 || '' }));
      return;
    }
    if (!base64) {
      if (selectedHub?.id && currentBikeData.hubBike?.bikeId) {
        setUploadingLabel(label);
        try {
          await GobikesApi.delete(
            `/vendors/hubs/${selectedHub.id}/bikes/${currentBikeData.hubBike.bikeId}/images/${label}`,
            { headers: { 'vendor-token': authCookie } }
          );
          setVehicleUploads(prev => { const next = { ...prev }; delete next[label]; return next; });
          setPhotoDisplayValues(prev => ({ ...prev, [label]: '' }));
          setCurrentBikeData(prev => {
            const imageData = { ...(prev.hubBike.imageData as Record<string, string> || {}) };
            delete imageData[label];
            return { ...prev, hubBike: { ...prev.hubBike, imageData } };
          });
        } catch (_) {
          setState(s => ({ ...s, error: true, message: 'Failed to delete image. Please try again.' }));
        }
        setUploadingLabel(null);
      }
      return;
    }
    if (!selectedHub?.id || !currentBikeData.hubBike?.bikeId) return;
    setUploadingLabel(label);
    try {
      const { data } = await GobikesApi.post(
        `/vendors/hubs/${selectedHub.id}/bikes/${currentBikeData.hubBike.bikeId}/images`,
        { label, image: base64 },
        { headers: { 'vendor-token': authCookie } }
      );
      if (data?.data) {
        setVehicleUploads(prev => ({ ...prev, [label]: data.data }));
        if (data.data.publicUrl) {
          setPhotoDisplayValues(prev => ({ ...prev, [label]: data.data.publicUrl }));
        }
      }
    } catch (_) {
      setState(s => ({ ...s, error: true, message: 'Failed to upload image. Please try again.' }));
    }
    setUploadingLabel(null);
  }

  function updateOverrideData(overrides: HubBikeOverrideModel[]) {
    setCurrentBikeData({ ...currentBikeData, overrides })
  }
  function addToDeletedOverridesSet(newId: number) {
    const newDeletedOverrides = currentBikeData.deletedOverrides;
    newDeletedOverrides.add(newId);
    setCurrentBikeData({ ...currentBikeData, deletedOverrides: newDeletedOverrides })
  }

  // Save bike details
  async function saveBikeDetails() {

    // Guard: hub must be selected
    if (!selectedHub?.id) {
      setState({ ...state, error: true, message: 'Hub not loaded. Please refresh and try again.' });
      return;
    }

    // Parse input and check for errors
    // const images = [];
    // for (const label of Object.keys(currentBikeData.upload)) {
    //   images.push({ label: label, data: currentBikeData.upload[label as HubBikeUploadLabels] })
    // }

    // Ensure enabled availability options have a price value (at least 0) so backend dependency check passes
    const prices: BikePrice = { ...currentBikeData.hubBike.prices };
    const availability = (currentBikeData.hubBike.availability || {}) as Record<string, boolean>;
    for (const key of Object.keys(availability)) {
      if (availability[key] && (prices as any)[key] === undefined) {
        (prices as any)[key] = 0;
      }
    }
    if (!currentBikeData.hubBike.needWeekendPrice) prices.weekEnd = undefined;

    const payload = {
      bikeId: currentBikeData.bike.id,
      ...currentBikeData.hubBike,
      prices,
      // quantity: currentBikeData.hubBike.bikes.length,
      discount: currentBikeData.hubBike.discount,
      ...(!currentBikeData.hubBike.needDeposit && { deposit: 0 }),
      // images,
      bikes: currentBikeData.hubBike.bikes
        .filter((bike: OneBike) => bike.rcNumber && bike.rcNumber.trim() !== '')
        .map((bike: OneBike) => {
          return bike;
        }),
      hubBikeOverrides: currentBikeData.overrides.map(o => ({
        id: o.id,
        startDate: o.startDate,
        endDate: o.endDate,
        // halfDay: o.prices.halfDay,
        fullDay: o.prices.fullDay,
        // weekEnd: o.prices.weekEnd,
        // week: o.prices.week,
        // fortnight: o.prices.fortnight,
        // month: o.prices.month,
        // quarterYear: o.prices.quarterYear,
        // halfYear: o.prices.halfYear,
        // fullYear: o.prices.fullYear,
        // deposit: o.deposit
      })),
      deletedHubBikeOverrides: Array.from(currentBikeData.deletedOverrides)
    };
    payload.activeQuantity = payload.quantity;
    payload.vendorActive = props.creating ? true : undefined;
    // TODO: Do UI checks

    // Set loading mode and try to save bike
    setState({ ...state, loading: true });
    try {
      await GobikesApi.request({
        url: `/vendors/hubs/${selectedHub.id}/bikes`,
        method: props.creating ? 'POST' : 'PUT',
        headers: { 'vendor-token': authCookie },
        data: payload
      })

      // Upload photos picked while creating, now that the hub bike exists
      if (props.creating) {
        let failedUploads = 0;
        for (const [label, image] of Object.entries(currentBikeData.upload)) {
          try {
            await GobikesApi.post(
              `/vendors/hubs/${selectedHub.id}/bikes/${currentBikeData.bike.id}/images`,
              { label, image },
              { headers: { 'vendor-token': authCookie } }
            );
          } catch (_) {
            failedUploads++;
          }
        }
        if (failedUploads) {
          setState({ ...state, error: true, loading: false, message: `Vehicle saved, but ${failedUploads} photo(s) failed to upload. Please add them from Edit.`, resetError: () => props.backFn(true) });
          return;
        }
      }
      setState({ ...state, error: false, message: 'Bike edited successfully!', loading: false })
      props.backFn(true);
    } catch (e: any) {
      const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
      setState({ ...state, error: true, message: errorMessage, loading: false, resetError: () => { setState({ ...state, error: false, loading: false, resetError: undefined }); } })
    }
  }

  // Return DOM
  if (state.error || state.loading)
    return (
      <DashboardLoader
        {...state}
        styles={DEFAULT_LOADER_STYLES}
      />
    )
  else return (
    <Fragment>
      <div className="gbk-paper-container vehicle-detail-form" >

        {/* Top spacing to clear the navbar on phone */}
        <Box sx={{ display: { xs: 'block', md: 'none' }, height: { xs: '50px', sm: '60px' }, width: 1 }} />

        {isPhone ? (
          <div style={{ padding: '4px 12px 100px' }}>
            {renderBikeDetails()}
            {renderEachBikeDetails()}
            {renderChargesDetails()}
            {renderKmPackagesDetails()}
            {renderRentalDetails()}
            {renderFutureRateDetails()}
            {renderSecurityDepositDetails()}
            {renderTncDetails()}
            {renderPhotoUploadContent()}
          </div>
        ) : (
          <Fragment>
            {renderBikeDetails()}<br />
            {renderEachBikeDetails()}<br />
            {renderChargesDetails()}<br />
            {renderKmPackagesDetails()}<br />
            {renderRentalDetails()}<br />
            {renderFutureRateDetails()}<br />
            {renderSecurityDepositDetails()}<br />
            {renderTncDetails()}<br />
            {renderPhotoUploadContent()}<br /><br /><br />
            <br />
          </Fragment>
        )}
      </div>

      {/* Action buttons */}
      {selectedHub?.inventoryLocked ? '' : (
        <Box className="vehicle-detail-actions" sx={{
          position: 'fixed',
          bottom: 0,
          left: { xs: 0, md: props.drawerWidth },
          width: { xs: '100vw', md: `calc(100vw - ${props.drawerWidth}px)` },
          padding: { xs: '10px 16px', md: '20px 40px' },
          paddingBottom: { xs: 'calc(10px + env(safe-area-inset-bottom, 0px))', md: 'calc(20px + env(safe-area-inset-bottom, 0px))' },
          background: '#fff',
          boxShadow: '0px -2px 6px #ddd',
          zIndex: 10,
          boxSizing: 'border-box'
        }}> {
            editMode
              ? (
                isPhone ? (
                  <Box sx={{ display: 'flex', gap: '10px' }}>
                    <Button fullWidth variant="contained" onClick={saveBikeDetails} sx={{ textTransform: 'none', fontWeight: 700, fontSize: '15px' }}>Save</Button>
                    <Button variant="outlined" onClick={e => { if (props.creating) props.backFn(false); else setEditMode(false); }} sx={{ flexShrink: 0, textTransform: 'none', fontWeight: 600 }}>Cancel</Button>
                  </Box>
                ) : (
                  <Fragment>
                    <Button className="thick-border" variant="contained" onClick={saveBikeDetails}>Save</Button>
                    <Button className="thick-border" variant="outlined" sx={{ marginLeft: 2 }} onClick={e => {
                      if (props.creating) props.backFn(false);
                      else setEditMode(false);
                    }}>Cancel</Button>
                  </Fragment>
                )
              )
              : (
                <Button className={isPhone ? '' : 'thick-border'} fullWidth={isPhone} variant="contained" onClick={e => setEditMode(true)} sx={isPhone ? { textTransform: 'none', fontWeight: 700 } : undefined}>Edit</Button>
              )
          }
        </Box>
      )}

      {renderDiscountModal()}
    </Fragment >
  )

  /*
   * Render utility functions beyond this point
   */

  // Shared phone card style tokens
  function phoneCardStyle() {
    return {
      card: { background: '#fff', borderRadius: 14, marginBottom: 14, overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.07)' } as CSSProperties,
      header: { padding: '10px 14px 8px', fontSize: 11, fontWeight: 700, color: '#999', textTransform: 'uppercase', letterSpacing: '0.6px' } as CSSProperties,
      field: { padding: '10px 14px 6px', borderTop: '1px solid #f2f2f2' } as CSSProperties,
      label: { fontSize: 11, color: '#999', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 4 } as CSSProperties,
    };
  }

  // High level bike details
  function renderBikeDetails() {
    if (isPhone) {
      const cs = phoneCardStyle();
      return (
        <div style={cs.card}>
          <div style={{ ...cs.header, display: 'flex', alignItems: 'center', gap: 8, padding: '12px 14px 10px', fontSize: 14, fontWeight: 700, color: '#333', textTransform: 'none', letterSpacing: 0 }}>
            <span className="gbk-icon-span clickable" onClick={() => props.backFn(false)} style={{ display: 'flex', alignItems: 'center', color: '#558b2f' }}><ArrowBack fontSize="small" /></span>
            Vehicle Details
          </div>
          <div style={cs.field}>
            <div style={cs.label}>Vehicle Name</div>
            {props.creating ? (
              <GobikesField
                type={GbkFieldType.SELECT}
                editMode={true}
                selectOptions={(state.bikeClassData || []).map(b => ({
                  value: b.id,
                  display: `${b.brand} ${b.name} ${b.vehicleType === VehicleType.CAR ? `(${b.engineType.charAt(0).toUpperCase() + b.engineType.substring(1).toLowerCase()})` : ''} ${b.vehicleType === VehicleType.CAR && b.transmission ? `(${b.transmission.charAt(0).toUpperCase() + b.transmission.substring(1).toLowerCase()})` : ''}`
                }))}
                value={currentBikeData.bike.id && currentBikeData.bike.id > 0 ? currentBikeData.bike.id.toString() : ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                updateFn={(v: number) => { updateCurrentBikeData({ ...(state && state.bikeClassMap && { ...state.bikeClassMap[v] }) }) }}
                gbkFieldSx={{ width: '100%' }}
              />
            ) : (
              <div style={{ fontSize: 14, fontWeight: 600, paddingTop: 4 }}>
                {`${currentBikeData.bike.brand} ${currentBikeData.bike.name}`}
              </div>
            )}
          </div>
          <div style={cs.field}>
            <div style={cs.label}>Quantity</div>
            <GobikesField
              type={GbkFieldType.NUMBER}
              editMode={editMode}
              value={currentBikeData.hubBike.quantity}
              updateFn={(v) => {
                const currentCount = currentBikeData.hubBike.bikes.length;
                if (v > currentCount) {
                  const newBikes = [...currentBikeData.hubBike.bikes];
                  for (let i = currentCount; i < v; i++) {
                    newBikes.push({ rcNumber: '', documentVerifed: false, odometer: 0, mfdYear: 0, newUploads: {} as HubBikePerQuantityImageUploads, lastServiced: new Date().getTime(), fastTag: false });
                  }
                  setCurrentBikeData({ ...currentBikeData, hubBike: { ...currentBikeData.hubBike, quantity: v, bikes: newBikes } });
                } else {
                  updateCurrentHubBikeData({ quantity: v });
                }
              }}
              gbkFieldSx={{ width: '100%' }}
            />
          </div>
        </div>
      );
    }
    return <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
      <div className="header">
        <Typography className="header-left" fontSize={18} fontWeight={500}>
          Vehicle Details
        </Typography>
      </div>
      <div className="content" >
        <div className="gbk-col" style={{ minWidth: 200, width: 300 }}>
          {props.creating ? (
            <GobikesField
              type={GbkFieldType.SELECT}
              label={'Vehicle Name'}
              editMode={true}
              selectOptions={
                (state.bikeClassData || []).map(
                  b => ({
                    value: b.id,
                    display: `${b.brand} ${b.name} ${b.vehicleType === VehicleType.CAR ? `(${b.engineType.charAt(0).toUpperCase() + b.engineType.substring(1).toLowerCase()})` : ''
                      } ${b.vehicleType === VehicleType.CAR && b.transmission ? `(${b.transmission.charAt(0).toUpperCase() + b.transmission.substring(1).toLowerCase()})` : ''}`
                  })
                )}
              value={currentBikeData.bike.id && currentBikeData.bike.id > 0 ? currentBikeData.bike.id.toString() : ''}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              updateFn={(v: number) => {
                updateCurrentBikeData({ ...(state && state.bikeClassMap && { ...state.bikeClassMap[v] }) })
              }}
            />
          ) : (
            <GobikesField
              type={GbkFieldType.STRING}
              label={'Vehicle Name'}
              editMode={false}
              value={`${currentBikeData.bike.brand} ${currentBikeData.bike.name}${currentBikeData.bike.vehicleType === VehicleType.CAR
                ? ` (${currentBikeData.bike.startingMethod === 'AUTO' ? 'Automatic' : currentBikeData.bike.startingMethod}, ${currentBikeData.bike.engineType?.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}${currentBikeData.bike.transmission ? `, ${currentBikeData.bike.transmission.charAt(0).toUpperCase() + currentBikeData.bike.transmission.substring(1).toLowerCase()}` : ''})`
                : ''}`}
            />
          )}
        </div>
        <div className="gbk-col" style={{ minWidth: 300 }}>
          <GobikesField
            type={GbkFieldType.NUMBER}
            label={'Quantity'}
            editMode={editMode}
            value={currentBikeData.hubBike.quantity}
            updateFn={(v) => {
              const currentCount = currentBikeData.hubBike.bikes.length;
              if (v > currentCount) {
                const newBikes = [...currentBikeData.hubBike.bikes];
                for (let i = currentCount; i < v; i++) {
                  newBikes.push({ rcNumber: '', documentVerifed: false, odometer: 0, mfdYear: 0, newUploads: {} as HubBikePerQuantityImageUploads, lastServiced: new Date().getTime(), fastTag: false });
                }
                setCurrentBikeData({ ...currentBikeData, hubBike: { ...currentBikeData.hubBike, quantity: v, bikes: newBikes } });
              } else {
                updateCurrentHubBikeData({ quantity: v });
              }
            }}
          />
          <br />
        </div>
      </div>
    </Paper>
  }

  // Per bike low level bike details
  function renderEachBikeDetails() {
    if (isPhone) {
      const cs = phoneCardStyle();
      const bikeFieldDefs = [
        { name: 'Vehicle Number', field: 'rcNumber', type: GbkFieldType.STRING },
        { name: 'Year of Manufacture', field: 'mfdYear', type: GbkFieldType.NUMBER, validateFn: betweenValuesValidationFn(new Date().getFullYear() - 20, new Date().getFullYear(), 'Select a valid year') },
        { name: 'Last Serviced Date', field: 'lastServiced', type: GbkFieldType.DATE, validateFn: lessThanValueValidationFn(new Date().getTime(), 'Select a past date') },
        { name: 'Odometer Reading', field: 'odometer', type: GbkFieldType.NUMBER },
        ...(currentBikeData.bike.vehicleType === VehicleType.CAR ? [{ name: 'Fast Tag', field: 'fastTag', type: GbkFieldType.SWITCH }] : []),
      ] as { name: string, field: keyof OneBike, type: any, validateFn?: (v: any) => string }[];
      return (
        <div style={cs.card}>
          <div style={cs.header}>Individual Vehicle Details</div>
          {currentBikeData.hubBike.bikes.map((b: OneBike, i: number) => (
            <div key={i} style={{ borderTop: i === 0 ? 'none' : '2px solid #f0f0f0', padding: '10px 14px 4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#558b2f' }}>Vehicle {i + 1}</div>
                {editMode && (
                  <span style={{ color: Colors.failureRed, cursor: 'pointer', fontSize: 13 }} onClick={() => {
                    const eachBikeDetail = [...currentBikeData.hubBike.bikes];
                    eachBikeDetail.splice(i, 1);
                    updateCurrentHubBikeData({ bikes: eachBikeDetail });
                  }}>Remove</span>
                )}
              </div>
              {bikeFieldDefs.map((f, fi) => (
                <div key={fi} style={{ marginBottom: 8 }}>
                  <div style={cs.label}>{f.name}</div>
                  <GobikesField
                    type={f.type}
                    editMode={editMode}
                    value={b[f.field]}
                    validateFn={f.validateFn}
                    selectOptions={[]}
                    updateFn={(v: any) => {
                      const eachBikeDetail = [...currentBikeData.hubBike.bikes];
                      eachBikeDetail[i][f.field] = v as never;
                      updateCurrentHubBikeData({ bikes: eachBikeDetail });
                    }}
                    gbkFieldSx={{ width: '100%' }}
                  />
                </div>
              ))}
            </div>
          ))}
          {editMode && (
            <div style={{ padding: '10px 14px 14px', borderTop: currentBikeData.hubBike.bikes.length ? '1px solid #f2f2f2' : 'none' }}>
              <Button variant="contained" fullWidth onClick={() => {
                const eachBikeDetail = [...currentBikeData.hubBike.bikes];
                eachBikeDetail.push({ rcNumber: '', documentVerifed: false, odometer: 0, mfdYear: 0, newUploads: {}, lastServiced: new Date().getTime(), fastTag: false });
                updateCurrentHubBikeData({ bikes: eachBikeDetail });
              }} sx={{ textTransform: 'none', fontWeight: 700 }}>
                <Add /> Add Vehicle
              </Button>
            </div>
          )}
        </div>
      );
    }
    return <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
      <div className="header">
        <Typography className="header-left" fontSize={18} fontWeight={500}>Individual Vehicle Details</Typography>
      </div>
      <div className="content gbk-scroll low-contrast" style={{ width: 'auto', overflowX: 'scroll', whiteSpace: 'nowrap' }}>

        {/* Visual element for existing bikes */}
        {
          ([{
            name: 'Vehicle Number',
            field: 'rcNumber',
            type: GbkFieldType.STRING
          }, {
            name: 'Year of Manufacture',
            field: 'mfdYear',
            type: GbkFieldType.NUMBER,
            validateFn: betweenValuesValidationFn(new Date().getFullYear() - 20, new Date().getFullYear(), 'Select a valid year'),
            width: 220
          }, {
            name: 'Last Serviced date',
            field: 'lastServiced',
            type: GbkFieldType.DATE,
            validateFn: lessThanValueValidationFn(new Date().getTime(), 'Select a past date'),
            width: 250
          }, {
            name: 'Odometer Reading',
            field: 'odometer',
            type: GbkFieldType.NUMBER,
            width: 200
          },
          ...(currentBikeData.bike.vehicleType === VehicleType.CAR ? [
            {
              name: 'Fast Tag',
              field: 'fastTag',
              type: GbkFieldType.SWITCH
            }
          ] : []),

            // }, {
            //   name: 'RC Upload',
            //   field: 'rcPhoto',
            //   type: GbkFieldType.UPLOAD,
            //   width: 250
            // }, {
            //   name: 'Pollution Certificate Upload',
            //   field: 'pollutionPhoto',
            //   type: GbkFieldType.UPLOAD,
            //   width: 250
            // }, {
            //   name: 'Pollution Expiry',
            //   field: 'pollutionExpiry',
            //   type: GbkFieldType.DATE,
            //   width: 200
            // }, {
            //   name: 'Insurance Copy',
            //   field: 'insurancePhoto',
            //   type: GbkFieldType.UPLOAD,
            //   width: 250
            // }, {
            // name: 'Insurance Expiry',
            // field: 'pollutionExpiry',
            // type: GbkFieldType.DATE,
            // width: 200
          ] as {
            name: string,
            field: keyof OneBike,
            type: GbkFieldType.NUMBER | GbkFieldType.STRING | GbkFieldType.PASSWORD | GbkFieldType.SELECT | GbkFieldType.UPLOAD | GbkFieldType.DATE | GbkFieldType.DATE_TIME | GbkFieldType.CHECKBOX | GbkFieldType.RADIO | GbkFieldType.SWITCH,
            validateFn?: (v: any) => string,
            width?: number
          }[]).map((f, index) => (
            <div key={index} className="gbk-col" style={{ minWidth: f.width ? f.width : 200, paddingBottom: 0 }}>
              {currentBikeData.hubBike.bikes.map((b: OneBike, i: number) => (
                <Fragment key={i}>
                  <GobikesField
                    type={f.type}
                    label={i === 0 ? f.name : ''}
                    editMode={editMode}
                    value={
                      f.type === GbkFieldType.UPLOAD ? b.newUploads[f.field as HubBikePerQuantityUploadLabels] : b[f.field]
                    }
                    selectOptions={[]}
                    validateFn={f.validateFn}
                    updateFn={(v: any) => {
                      const eachBikeDetail = currentBikeData.hubBike.bikes;
                      if (f.type === GbkFieldType.UPLOAD) {
                        eachBikeDetail[i].newUploads[f.field as HubBikePerQuantityUploadLabels] = v as never;
                      } else {
                        eachBikeDetail[i][f.field] = v as never;
                      }
                      updateCurrentHubBikeData({ bikes: eachBikeDetail });
                    }}
                    {...(f.type === GbkFieldType.UPLOAD && { smallUpload: true, tag: `${f.name}-${i}` })}
                  />
                  <br />
                </Fragment>
              ))}
            </div>
          ))
        }
        {editMode ? (
          <div className="gbk-col" style={{ minWidth: 50, paddingBottom: 0 }}>
            {currentBikeData.hubBike.bikes.map((b: OneBike, i: number) => (
              <Fragment key={i}>
                <div className="gbk-field">
                  {i === 0 ? <Fragment><label className="label">Delete</label> <br /></Fragment> : ''}
                  <label className="value" style={{ textAlign: 'center', padding: '11px 0 34px 0' }}>
                    <span className="gbk-icon-span clickable large" onClick={() => {
                      const eachBikeDetail = currentBikeData.hubBike.bikes;
                      eachBikeDetail.splice(i, 1);
                      updateCurrentHubBikeData({ bikes: eachBikeDetail });
                    }} >
                      <Cancel htmlColor={Colors.failureRed} />
                    </span>
                  </label>
                </div>
                <br />
              </Fragment>
            ))}
          </div>
        ) : ''}

        {/* Add bike button */}
        {editMode ? (
          <Fragment>
            <br />
            <Button
              variant='contained'
              onClick={() => {
                const eachBikeDetail = currentBikeData.hubBike.bikes;
                eachBikeDetail.push({ rcNumber: '', documentVerifed: false, odometer: 0, mfdYear: 0, newUploads: {}, lastServiced: new Date().getTime(), fastTag: false });
                updateCurrentHubBikeData({ bikes: eachBikeDetail });
              }}
            >
              <Add /> Add Vehicle
            </Button>
          </Fragment>
        ) : ''}

      </div>
    </Paper >
  }

  // Bike charges details
  function renderChargesDetails() {
    if (isPhone) {
      const cs = phoneCardStyle();
      return (
        <div style={cs.card}>
          <div style={cs.header}>Charges</div>
          <div style={cs.field}>
            <div style={cs.label}>Speed Limit (Km/h)</div>
            <GobikesField type={GbkFieldType.NUMBER} editMode={editMode} value={currentBikeData.hubBike.speedLimit}
              updateFn={(v: number) => updateCurrentHubBikeData({ speedLimit: v })}
              validateFn={nonNegativeValidationFn()} gbkFieldSx={{ width: '100%' }} />
          </div>
          <div style={cs.field}>
            <div style={cs.label}>Late Penalty (₹/hour)</div>
            <GobikesField type={GbkFieldType.NUMBER} editMode={editMode} value={currentBikeData.hubBike.penalty?.late || 0}
              updateFn={(v: number) => updateCurrentHubBikeData({ penalty: { ...currentBikeData.hubBike.penalty, late: v } })}
              validateFn={nonNegativeValidationFn()}
              inputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              gbkFieldSx={{ width: '100%' }} />
          </div>
          <div style={cs.field}>
            <div style={cs.label}>Distance Penalty (₹/Km)</div>
            <GobikesField type={GbkFieldType.NUMBER} editMode={editMode} value={currentBikeData.hubBike.penalty?.distance || 0}
              updateFn={(v: number) => updateCurrentHubBikeData({ penalty: { ...currentBikeData.hubBike.penalty, distance: v } })}
              validateFn={nonNegativeValidationFn()}
              inputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              gbkFieldSx={{ width: '100%' }} />
          </div>
        </div>
      );
    }
    return <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
      <div className="header">
        <Typography className="header-left" fontSize={18} fontWeight={500}>Charges</Typography>
      </div>
      <div className="content" >
        <div className="gbk-col" style={{ minWidth: 300 }}>
          <GobikesField
            type={GbkFieldType.NUMBER}
            label={'Speed Limit (Km/h)'}
            editMode={editMode}
            value={currentBikeData.hubBike.speedLimit}
            updateFn={(v: number) => {
              updateCurrentHubBikeData({ speedLimit: v })
            }}
            validateFn={nonNegativeValidationFn()}
          />
          <br />
        </div>
        <div className="gbk-col" style={{ minWidth: 300 }}>
          <GobikesField
            type={GbkFieldType.NUMBER}
            label={'Late penalty (₹/hour)'}
            editMode={editMode}
            value={currentBikeData.hubBike.penalty?.late || 0}
            updateFn={(v: number) => {
              updateCurrentHubBikeData({ penalty: { ...currentBikeData.hubBike.penalty, late: v } })
            }}
            validateFn={nonNegativeValidationFn()}
            inputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
          />
          <br />
        </div>
        <div className="gbk-col" style={{ minWidth: 300 }}>
          <GobikesField
            type={GbkFieldType.NUMBER}
            label={'Distance penalty (₹/Km)'}
            editMode={editMode}
            value={currentBikeData.hubBike.penalty?.distance || 0}
            updateFn={(v: number) => {
              updateCurrentHubBikeData({ penalty: { ...currentBikeData.hubBike.penalty, distance: v } })
            }}
            validateFn={nonNegativeValidationFn()}
            inputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
          />
          <br />
        </div>
      </div>
    </Paper>
  }

  // Extra km packages
  function renderKmPackagesDetails() {
    const kmPackages = currentBikeData.hubBike.kmPackages || [];
    if (isPhone) {
      const cs = phoneCardStyle();
      return (
        <div style={cs.card}>
          <div style={cs.header}>Extra KM Packages</div>
          {!kmPackages.length ? (
            <div style={{ padding: '12px 14px', color: '#999', fontSize: 13, fontStyle: 'italic' }}>
              {editMode ? 'Tap "Add" to create an extra km package' : 'No extra km packages specified'}
            </div>
          ) : kmPackages.map((kmPackage, i) => (
            <div key={i} style={{ ...cs.field, borderTop: i === 0 ? '1px solid #f2f2f2' : '2px solid #f0f0f0' }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={cs.label}>Extra Km</div>
                  <GobikesField type={GbkFieldType.NUMBER} editMode={editMode} value={kmPackage.extraKm}
                    updateFn={(v: number) => { const n = [...kmPackages]; n[i] = { ...n[i], extraKm: v }; updateCurrentHubBikeData({ kmPackages: n }); }}
                    validateFn={nonNegativeValidationFn()} gbkFieldSx={{ width: '100%' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={cs.label}>Charge</div>
                  <GobikesField type={GbkFieldType.NUMBER} editMode={editMode} value={kmPackage.charge}
                    updateFn={(v: number) => { const n = [...kmPackages]; n[i] = { ...n[i], charge: v }; updateCurrentHubBikeData({ kmPackages: n }); }}
                    validateFn={nonNegativeValidationFn()}
                    inputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    gbkFieldSx={{ width: '100%' }} />
                </div>
                {editMode && (
                  <span style={{ color: Colors.failureRed, cursor: 'pointer', fontSize: 13, paddingTop: 28, flexShrink: 0 }}
                    onClick={() => { const n = [...kmPackages]; n.splice(i, 1); updateCurrentHubBikeData({ kmPackages: n }); }}>
                    Remove
                  </span>
                )}
              </div>
              {editMode && currentBikeData.hubBike.penalty?.distance ? (
                <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                  Recommended: less than ₹{(0.8 * kmPackage.extraKm * (currentBikeData.hubBike.penalty.distance || 0)).toFixed(2)}
                </div>
              ) : null}
            </div>
          ))}
          {editMode && (
            <div style={{ padding: '10px 14px 14px', borderTop: '1px solid #f2f2f2' }}>
              <Button variant="contained" fullWidth disabled={kmPackages.length >= 5}
                onClick={() => updateCurrentHubBikeData({ kmPackages: [...kmPackages, { extraKm: 0, charge: 0 }] })}
                sx={{ textTransform: 'none', fontWeight: 700 }}>
                <Add /> Add km package
              </Button>
            </div>
          )}
        </div>
      );
    }
    return <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
      <div className="header">
        <Typography className="header-left" fontSize={18} fontWeight={500}>Extra KM Packages</Typography>
      </div>
      <div className="content">
        <div className="gbk-col" style={{ minWidth: 600 }}>
          <br />
          {!kmPackages.length ? (
            <Fragment>
              <Typography variant='caption' color={'gray'}>
                <i>{editMode ? 'Click "Add" to create an extra km package' : 'No extra km packages specified'}</i>
              </Typography>
              <br /><br />
            </Fragment>
          ) : (
            <Fragment>
              {kmPackages.map((kmPackage, i) => (
                <Fragment key={i}>
                  <Box className="gbk-row" sx={{ minWidth: { xs: 250, md: 700 } }}>
                    <GobikesField
                      type={GbkFieldType.NUMBER}
                      label={'Extra Km'}
                      editMode={editMode}
                      value={kmPackage.extraKm}
                      updateFn={(v: number) => {
                        const newKmPackages = [...kmPackages];
                        newKmPackages[i] = { ...newKmPackages[i], extraKm: v };
                        updateCurrentHubBikeData({ kmPackages: newKmPackages });
                      }}
                      validateFn={nonNegativeValidationFn()}
                    />
                    <GobikesField
                      type={GbkFieldType.NUMBER}
                      label={'Extra Charge'}
                      editMode={editMode}
                      value={kmPackage.charge}
                      updateFn={(v: number) => {
                        const newKmPackages = [...kmPackages];
                        newKmPackages[i] = { ...newKmPackages[i], charge: v };
                        updateCurrentHubBikeData({ kmPackages: newKmPackages });
                      }}
                      validateFn={nonNegativeValidationFn()}
                      inputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    />
                    {editMode && (
                      <span
                        className="gbk-icon-span clickable"
                        style={{ color: Colors.failureRed, verticalAlign: 'bottom', lineHeight: '54px', marginLeft: '8px' }}
                        onClick={() => {
                          const newKmPackages = [...kmPackages];
                          newKmPackages.splice(i, 1);
                          updateCurrentHubBikeData({ kmPackages: newKmPackages });
                        }}
                      >
                        <Cancel />
                      </span>
                    )}
                  </Box>
                  {editMode && currentBikeData.hubBike.penalty?.distance ? (
                    <Typography variant='caption' color={'gray'}>
                      Recommended: less than ₹{(0.8 * kmPackage.extraKm * (currentBikeData.hubBike.penalty.distance || 0)).toFixed(2)}
                    </Typography>
                  ) : null}
                  <br />
                </Fragment>
              ))}
              <br />
            </Fragment>
          )}
          {editMode && (
            <Button
              variant='contained'
              disabled={kmPackages.length >= 5}
              onClick={() => {
                const newKmPackages: KmPackage[] = [...kmPackages, { extraKm: 0, charge: 0 }];
                updateCurrentHubBikeData({ kmPackages: newKmPackages });
              }}
            >
              <Add />
              Add km package
            </Button>
          )}
        </div>
      </div>
    </Paper>
  }

  function openDiscountModal() {
    const discount = currentBikeData.hubBike.discount;
    setDiscountModal({
      open: true,
      discountType: discount?.discountType || 'NONE',
      value: discount?.discountType === HubBikeDiscountType.PERCENT ? (discount.value || 0) * 100 : discount?.value,
      maxValue: discount?.maxValue
    });
  }

  function renderDiscountButton() {
    const hasDiscount = Boolean(currentBikeData.hubBike.discount);
    return (
      <Button
        size="small"
        variant={hasDiscount ? 'contained' : 'outlined'}
        disabled={!editMode}
        startIcon={<LocalOffer fontSize="small" />}
        onClick={openDiscountModal}
        sx={{ textTransform: 'none', fontWeight: 600, fontSize: isPhone ? '11px' : undefined, borderRadius: '10px' }}
      >
        {hasDiscount ? 'Discount Added' : 'Discount'}
      </Button>
    );
  }

  function isDiscountModalValueValid() {
    if (!discountModal.discountType || discountModal.discountType === 'NONE') return true;
    if (!discountModal.value || discountModal.value <= 0) return false;
    if (discountModal.discountType === HubBikeDiscountType.PERCENT && discountModal.value > 100) return false;
    return true;
  }

  function renderDiscountModalContent() {
    return (
      <Fragment>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, fontSize: '16px' }}>Discount</Typography>
        <GobikesField
          type={GbkFieldType.RADIO}
          editMode={editMode}
          value={discountModal.discountType || 'NONE'}
          updateFn={(v: string) => setDiscountModal({ ...discountModal, discountType: v })}
          selectOptions={[
            { value: 'NONE', display: 'No Discount' },
            { value: HubBikeDiscountType.FLAT, display: 'Flat Discount' },
            { value: HubBikeDiscountType.PERCENT, display: 'Percentage Discount' },
          ]}
          gbkFieldSx={{ width: '100%', marginBottom: '12px' }}
        />
        {discountModal.discountType && discountModal.discountType !== 'NONE' && (
          <Fragment>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#999', fontWeight: 600, marginBottom: 4 }}>
                {discountModal.discountType === HubBikeDiscountType.PERCENT ? 'Discount Percentage' : 'Discount Amount'}
              </div>
              <GobikesField
                type={GbkFieldType.NUMBER}
                editMode={editMode}
                value={discountModal.value || 0}
                updateFn={(v: number) => setDiscountModal({ ...discountModal, value: v })}
                validateFn={discountModal.discountType === HubBikeDiscountType.PERCENT ? betweenValuesValidationFn(0, 100) : nonNegativeValidationFn()}
                inputProps={discountModal.discountType === HubBikeDiscountType.PERCENT
                  ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                  : { startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                displaySuffix={discountModal.discountType === HubBikeDiscountType.PERCENT ? '%' : undefined}
                displayPrefix={discountModal.discountType === HubBikeDiscountType.PERCENT ? undefined : '₹'}
                gbkFieldSx={{ width: '100%' }}
              />
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 12, color: '#999', fontWeight: 600, marginBottom: 4 }}>Max Discount Value</div>
              <GobikesField
                type={GbkFieldType.NUMBER}
                editMode={editMode}
                value={discountModal.maxValue || 0}
                updateFn={(v: number) => setDiscountModal({ ...discountModal, maxValue: v })}
                validateFn={nonNegativeValidationFn()}
                inputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                displayPrefix='₹'
                gbkFieldSx={{ width: '100%' }}
              />
            </div>
          </Fragment>
        )}
        <Box sx={{ display: 'flex', gap: 1, marginTop: '10px' }}>
          {editMode ? (
            <Fragment>
              <Button
                variant="contained"
                disabled={!isDiscountModalValueValid()}
                sx={{ flex: 1, borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                onClick={() => {
                  const discount = (!discountModal.discountType || discountModal.discountType === 'NONE')
                    ? undefined
                    : {
                      discountType: discountModal.discountType as HubBikeDiscountType,
                      value: discountModal.discountType === HubBikeDiscountType.PERCENT ? (discountModal.value || 0) / 100 : discountModal.value || 0,
                      maxValue: discountModal.maxValue
                    };
                  updateCurrentHubBikeData({ discount });
                  setDiscountModal({ open: false });
                }}
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                sx={{ borderRadius: '10px', textTransform: 'none' }}
                onClick={() => setDiscountModal({ open: false })}
              >
                Cancel
              </Button>
            </Fragment>
          ) : (
            <Button
              variant="outlined"
              sx={{ flex: 1, borderRadius: '10px', textTransform: 'none' }}
              onClick={() => setDiscountModal({ open: false })}
            >
              Close
            </Button>
          )}
        </Box>
      </Fragment>
    );
  }

  function renderDiscountModal() {
    if (isPhone) {
      return (
        <Drawer
          anchor="bottom"
          open={discountModal.open}
          onClose={() => setDiscountModal({ open: false })}
          PaperProps={{ sx: { borderTopLeftRadius: 18, borderTopRightRadius: 18, padding: '20px 20px 40px' } }}
        >
          {renderDiscountModalContent()}
        </Drawer>
      );
    }
    return (
      <Dialog open={discountModal.open} onClose={() => setDiscountModal({ open: false })} maxWidth="xs" fullWidth>
        <DialogContent>
          {renderDiscountModalContent()}
        </DialogContent>
      </Dialog>
    );
  }

  // Rental details
  function renderRentalDetails() {
    const rentalFields = [
      { name: '3 Hours Price', field: 'eighthDay' as keyof BikePrice },
      { name: '6 Hours Price', field: 'quarterDay' as keyof BikePrice },
      { name: 'Half Day Price', field: 'halfDay' as keyof BikePrice },
      { name: 'Full Day Price', field: 'fullDay' as keyof BikePrice },
      ...(currentBikeData.hubBike.needWeekendPrice ? [{ name: 'Weekend Price', field: 'weekEnd' as keyof BikePrice }] : []),
      { name: 'Weekly Price', field: 'week' as keyof BikePrice },
      { name: '15 Days Price', field: 'fortnight' as keyof BikePrice },
      { name: '30 Days Price', field: 'month' as keyof BikePrice },
      { name: '3 Month Package', field: 'quarterYear' as keyof BikePrice },
      { name: '6 Month Package', field: 'halfYear' as keyof BikePrice },
      { name: '12 Month Package', field: 'fullYear' as keyof BikePrice },
    ];
    if (isPhone) {
      const cs = phoneCardStyle();
      return (
        <div style={cs.card}>
          <div style={{ ...cs.header, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Rentals</span>
            {renderDiscountButton()}
          </div>
          {rentalFields.map((f, i) => (
            <Fragment key={i}>
              <div style={{ ...cs.field, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Checkbox
                  checked={Boolean(f.field === 'weekEnd' ? true : currentBikeData.hubBike.availability[f.field])}
                  disabled={f.field === 'weekEnd' || !editMode}
                  onChange={(_: any, v: boolean) => updateCurrentHubBikeData({ availability: { ...currentBikeData.hubBike.availability, [f.field]: v } })}
                  size="small"
                  style={{ padding: 4, flexShrink: 0 }}
                />
                <div style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{f.name}</div>
                <div style={{ width: 105, flexShrink: 0 }}>
                  <GobikesField type={GbkFieldType.NUMBER} editMode={editMode}
                    value={currentBikeData.hubBike.prices[f.field] || 0}
                    updateFn={(v: number) => updateCurrentHubBikeData({ prices: { ...currentBikeData.hubBike.prices, [f.field]: v } })}
                    inputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    validateFn={nonNegativeValidationFn()} displayPrefix='₹'
                    gbkFieldSx={{ width: '100%' }} />
                </div>
                <div style={{ width: 90, flexShrink: 0 }}>
                  <GobikesField type={GbkFieldType.NUMBER} editMode={editMode}
                    value={currentBikeData.hubBike.distanceLimits?.[f.field] || 0}
                    updateFn={(v: number) => updateCurrentHubBikeData({ distanceLimits: { ...currentBikeData.hubBike.distanceLimits, [f.field]: v } })}
                    inputProps={{ endAdornment: <InputAdornment position="end">Km</InputAdornment> }}
                    validateFn={nonNegativeValidationFn()} displaySuffix='Km'
                    gbkFieldSx={{ width: '100%' }} />
                </div>
              </div>
              {f.field === 'fullDay' && (
                <div style={{ padding: '6px 14px 10px', borderTop: '1px solid #f2f2f2', background: '#fafafa' }}>
                  <GobikesField type={GbkFieldType.RADIO} editMode={editMode}
                    value={currentBikeData.hubBike.needWeekendPrice !== undefined ? currentBikeData.hubBike.needWeekendPrice : (Number(currentBikeData.hubBike.prices.weekEnd) > 0 ? 1 : 0)}
                    updateFn={(v: number) => updateCurrentHubBikeData({ needWeekendPrice: Number(v) })}
                    selectOptions={[{ value: 0, display: 'Same price for weekend' }, { value: 1, display: 'Different weekend price' }]}
                    gbkFieldSx={{ width: '100%' }} />
                </div>
              )}
            </Fragment>
          ))}
        </div>
      );
    }
    return <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
      <div className="header">
        <Typography className="header-left" fontSize={18} fontWeight={500}>Rentals</Typography>
        <div className="header-right">{renderDiscountButton()}</div>
      </div>
      <Box className="content" sx={{ paddingLeft: { xs: 10, md: 30 } }} >
        {
          ([
            { name: '3 Hours Price', field: 'eighthDay' },
            { name: '6 Hours Price', field: 'quarterDay' },
            { name: 'Half Day Price', field: 'halfDay' },
            { name: 'Full Day Price', field: 'fullDay' },
            ...(currentBikeData.hubBike.needWeekendPrice ? [{ name: 'Weekend Price', field: 'weekEnd' }] : []),
            { name: 'Weekly Price', field: 'week' },
            { name: '15 Days Price', field: 'fortnight' },
            { name: '30 Days Price', field: 'month' },
            { name: '3 Month Package', field: 'quarterYear' },
            { name: '6 Month Package', field: 'halfYear' },
            { name: '12 Month Package', field: 'fullYear' },
          ] as {
            name: string,
            field: keyof BikePrice,
          }[]).map((f, i) => (
            <Fragment key={i} >
              <Box className="gbk-col" sx={{ width: { xs: 160, md: 250 } }}>
                <GobikesField
                  type={GbkFieldType.CHECKBOX}
                  label={f.name}
                  editMode={editMode}
                  disabled={f.field === 'weekEnd'}
                  value={f.field === 'weekEnd' ? true : currentBikeData.hubBike.availability[f.field]}
                  updateFn={(v: number) => {
                    updateCurrentHubBikeData({ availability: { ...currentBikeData.hubBike.availability, [f.field]: v } })
                  }}
                />
              </Box>
              <Box className="gbk-col" sx={{ width: { xs: 150, md: 200 } }}>
                <GobikesField
                  type={GbkFieldType.NUMBER}
                  editMode={editMode}
                  value={currentBikeData.hubBike.prices[f.field] || 0}
                  updateFn={(v: number) => {
                    updateCurrentHubBikeData({ prices: { ...currentBikeData.hubBike.prices, [f.field]: v } })
                  }}
                  inputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                  validateFn={nonNegativeValidationFn()}
                  displayPrefix='₹'
                />
                <br />
              </Box>
              <Box className="gbk-col" sx={{ width: { xs: '100%', md: '350px' } }}>
                <Typography sx={{ display: 'inline-block', paddingTop: '8px', verticalAlign: 'bottom', height: '55px', paddingRight: { xs: '70px', md: '20px' }, paddingLeft: { xs: '30px', md: 0 } }}>Km limit</Typography>
                <Box sx={{ width: { xs: '150px', md: '200px', paddingLeft: { xs: '40px', md: 0 } }, display: 'inline-block', verticalAlign: 'top' }}>
                  <GobikesField
                    type={GbkFieldType.NUMBER}
                    editMode={editMode}
                    value={currentBikeData.hubBike.distanceLimits?.[f.field] || 0}
                    updateFn={(v: number) => {
                      updateCurrentHubBikeData({ distanceLimits: { ...currentBikeData.hubBike.distanceLimits, [f.field]: v } })
                    }}
                    inputProps={{ endAdornment: <InputAdornment position="end">Km</InputAdornment> }}
                    validateFn={nonNegativeValidationFn()}
                    displaySuffix='Km'
                  />
                </Box>
                <br />
              </Box>
              <br />
              {f.field === 'fullDay' ? (
                <Box className="gbk-col" sx={{ width: { xs: 400, md: 400 } }}>
                  <GobikesField
                    type={GbkFieldType.RADIO}
                    editMode={editMode}
                    value={currentBikeData.hubBike.needWeekendPrice !== undefined ? currentBikeData.hubBike.needWeekendPrice : (Number(currentBikeData.hubBike.prices.weekEnd) > 0 ? 1 : 0)}
                    updateFn={(v: number) => {
                      updateCurrentHubBikeData({ needWeekendPrice: Number(v) })
                    }}
                    selectOptions={[
                      { value: 0, display: 'Use same price for weekend' },
                      { value: 1, display: 'I need a different weekend price' }
                    ]}
                  />
                </Box>
              ) : ''}
              {i < 8 && !(f.field === 'fullDay' && currentBikeData.hubBike.needWeekendPrice) ? <div style={{ borderTop: '1px solid #ddd', height: '20px' }}></div> : ''}
              {f.field === 'fullDay' && currentBikeData.hubBike.needWeekendPrice ? (<br />) : ''}
            </Fragment>
          ))
        }
      </Box>
    </Paper>
  }

  // Future rate details
  function renderFutureRateDetails() {
    if (isPhone) {
      const cs = phoneCardStyle();
      return (
        <div style={cs.card}>
          <div style={cs.header}>Future Rates</div>
          {!currentBikeData.overrides.length && !editMode ? (
            <div style={{ padding: '10px 14px', color: '#999', fontSize: 13 }}>No future rates added</div>
          ) : (
            currentBikeData.overrides.map((o: HubBikeOverrideModel, i: number) => (
              <div key={i} style={{ borderTop: '1px solid #f2f2f2', padding: '10px 14px 6px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#444', marginBottom: 8 }}>Rate {i + 1}</div>
                <div style={cs.label}>Start Date</div>
                <GobikesField type={GbkFieldType.DATE_TIME} label={''} editMode={editMode}
                  minDateTime={DateTime.now().plus({ hours: 1 }).set({ minute: 0 })}
                  value={DateTime.fromMillis(Number(o.startDate))}
                  updateFn={(v: any) => { const d = currentBikeData.overrides; d[i].startDate = v; updateOverrideData(d); }}
                  gbkFieldSx={{ width: '100%' }} />
                <div style={{ ...cs.label, marginTop: 6 }}>End Date</div>
                <GobikesField type={GbkFieldType.DATE_TIME} label={''} editMode={editMode}
                  minDateTime={DateTime.fromMillis(Math.max(DateTime.now().plus({ hours: 1 }).set({ minute: 0 }).toMillis(), o.startDate || 0))}
                  value={DateTime.fromMillis(Number(o.endDate))}
                  updateFn={(v: any) => { const d = currentBikeData.overrides; d[i].endDate = v; updateOverrideData(d); }}
                  gbkFieldSx={{ width: '100%' }} />
                <div style={{ ...cs.label, marginTop: 6 }}>Full Day Price</div>
                <GobikesField type={GbkFieldType.NUMBER} label={''} editMode={editMode}
                  value={o.prices.fullDay}
                  validateFn={greaterThanValueValidationFn(0, 'Enter non-zero value')}
                  updateFn={(v: any) => { const d = currentBikeData.overrides; d[i].prices.fullDay = v; updateOverrideData(d); }}
                  gbkFieldSx={{ width: '100%' }} />
                {editMode && (
                  <div style={{ textAlign: 'right', marginTop: 2, marginBottom: 4 }}>
                    <span style={{ color: Colors.failureRed, fontSize: 13, cursor: 'pointer', fontWeight: 600 }}
                      onClick={() => { const d = currentBikeData.overrides; if (d[i].id) addToDeletedOverridesSet(d[i].id as number); d.splice(i, 1); updateOverrideData(d); }}>
                      Remove
                    </span>
                  </div>
                )}
              </div>
            ))
          )}
          {editMode && (
            <div style={{ padding: '10px 14px' }}>
              <Button variant='contained' fullWidth onClick={() => {
                const d = currentBikeData.overrides;
                d.push({ startDate: DateTime.now().startOf('hour').plus({ hours: 1 }).toMillis(), endDate: DateTime.now().startOf('hour').plus({ hours: 1, days: 1 }).toMillis(), prices: {} });
                updateOverrideData(d);
              }}><Add /> Add New Future Rate</Button>
            </div>
          )}
        </div>
      );
    }
    return <Paper key={'future-rates'} className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
      <div className="header">
        <Typography className="header-left" fontSize={18} fontWeight={500}>Future Rates</Typography>
      </div>
      <div className="content gbk-scroll low-contrast" style={{ width: 'auto', overflowX: 'scroll', whiteSpace: 'nowrap' }}>
        {!currentBikeData.overrides.length && !editMode ? (
          <span className="roboto grey-text-medium">No future rates added</span>
        ) :
          ([{
            name: 'Start Date',
            field: 'startDate',
            type: GbkFieldType.DATE_TIME,
            width: 300
          }, {
            name: 'End Date',
            field: 'endDate',
            type: GbkFieldType.DATE_TIME,
            width: 300
            // }, {
            //   name: 'Security Deposit',
            //   field: 'deposit',
            //   type: GbkFieldType.NUMBER,
            //   validateFn: greaterThanValueValidationFn(0, 'Enter non-zero value'),
            //   width: 220
            // }, {
            //   name: 'Half Day Price',
            //   field: 'halfDay',
            //   type: GbkFieldType.NUMBER,
            //   validateFn: greaterThanValueValidationFn(0, 'Enter non-zero value'),
            //   width: 220
          }, {
            name: 'Full Day Price',
            field: 'fullDay',
            type: GbkFieldType.NUMBER,
            validateFn: greaterThanValueValidationFn(0, 'Enter non-zero value'),
            width: 220
            // }, {
            //   name: 'Weekend Price',
            //   field: 'weekEnd',
            //   type: GbkFieldType.NUMBER,
            //   validateFn: greaterThanValueValidationFn(0, 'Enter non-zero value'),
            //   width: 220
            // }, {
            //   name: 'Weekly Price',
            //   field: 'week',
            //   type: GbkFieldType.NUMBER,
            //   validateFn: greaterThanValueValidationFn(0, 'Enter non-zero value'),
            //   width: 220
            // }, {
            //   name: '15 Days Price',
            //   field: 'fortnight',
            //   type: GbkFieldType.NUMBER,
            //   validateFn: greaterThanValueValidationFn(0, 'Enter non-zero value'),
            //   width: 220
            // }, {
            //   name: '30 Days Price',
            //   field: 'month',
            //   type: GbkFieldType.NUMBER,
            //   validateFn: greaterThanValueValidationFn(0, 'Enter non-zero value'),
            //   width: 220
            // }, {
            //   name: '3 Month Package',
            //   field: 'quarterYear',
            //   type: GbkFieldType.NUMBER,
            //   validateFn: greaterThanValueValidationFn(0, 'Enter non-zero value'),
            //   width: 220
            // }, {
            //   name: '6 Month Package',
            //   field: 'halfYear',
            //   type: GbkFieldType.NUMBER,
            //   validateFn: greaterThanValueValidationFn(0, 'Enter non-zero value'),
            //   width: 220
            // }, {
            //   name: '12 Month Package',
            //   field: 'fullYear',
            //   type: GbkFieldType.NUMBER,
            //   validateFn: greaterThanValueValidationFn(0, 'Enter non-zero value'),
            //   width: 220
          }] as {
            name: string,
            field: 'startDate' | 'endDate' | 'deposit' | keyof BikePrice,
            type: GbkFieldType.NUMBER | GbkFieldType.STRING | GbkFieldType.PASSWORD | GbkFieldType.SELECT | GbkFieldType.UPLOAD | GbkFieldType.DATE | GbkFieldType.DATE_TIME | GbkFieldType.CHECKBOX | GbkFieldType.RADIO | GbkFieldType.SWITCH,
            validateFn?: (v: any) => string,
            width?: number
          }[]).map((f, index) => (
            <div key={index} className="gbk-col" style={{ minWidth: f.width ? f.width : 200, paddingBottom: 0 }}>
              {currentBikeData.overrides.map((o: HubBikeOverrideModel, i: number) => (
                <Fragment key={i}>
                  <GobikesField
                    type={f.type}
                    label={i === 0 ? f.name : ''}
                    editMode={editMode}
                    minDateTime={
                      f.field === 'startDate'
                        ? DateTime.now().plus({ hours: 1 }).set({ minute: 0 })
                        : DateTime.fromMillis(Math.max(DateTime.now().plus({ hours: 1 }).set({ minute: 0 }).toMillis(), o.startDate || 0))
                    }
                    value={
                      f.field === 'startDate' || f.field === 'endDate' || f.field === 'deposit'
                        ? (
                          f.type === GbkFieldType.DATE_TIME && (f.field === 'startDate' || f.field === 'endDate') ? DateTime.fromMillis(Number(o[f.field])) : o[f.field]
                        )
                        : o.prices[f.field]
                    }
                    selectOptions={[]}
                    validateFn={f.validateFn}
                    updateFn={(v: any) => {
                      const currentOverrideData = currentBikeData.overrides;
                      if (f.field === 'startDate' || f.field === 'endDate' || f.field === 'deposit') {
                        currentOverrideData[i][f.field] = v;
                      } else {
                        currentOverrideData[i].prices[f.field] = v;
                      }
                      updateOverrideData(currentOverrideData);
                    }}
                  />
                  <br />
                </Fragment>
              ))}
            </div>
          ))
        }
        {editMode ? (
          <div className="gbk-col" style={{ minWidth: 50, paddingBottom: 0 }}>
            {currentBikeData.overrides.map((o: HubBikeOverrideModel, i: number) => (
              <Fragment key={i}>
                <div className="gbk-field">
                  {i === 0 ? <Fragment><label className="label">Delete</label> <br /></Fragment> : ''}
                  <label className="value" style={{ textAlign: 'center', padding: '11px 0 34px 0' }}>
                    <span className="gbk-icon-span clickable large" onClick={() => {
                      const currentOverrideData = currentBikeData.overrides;
                      if (currentOverrideData[i].id) {
                        addToDeletedOverridesSet(currentOverrideData[i].id as number);
                      }
                      currentOverrideData.splice(i, 1);
                      updateOverrideData(currentOverrideData);
                    }} >
                      <Cancel htmlColor={Colors.failureRed} />
                    </span>
                  </label>
                </div>
                <br />
              </Fragment>
            ))}
          </div>
        ) : ''}

        {/* Add bike button */}
        {editMode ? (
          <Fragment>
            <br />
            <Button
              variant='contained'
              onClick={() => {
                const currentOverrideData = currentBikeData.overrides;
                currentOverrideData.push({ startDate: DateTime.now().startOf('hour').plus({ hours: 1 }).toMillis(), endDate: DateTime.now().startOf('hour').plus({ hours: 1, days: 1 }).toMillis(), prices: {} })
                updateOverrideData(currentOverrideData);
              }}
            >
              <Add /> Add New Future Rate
            </Button>
          </Fragment>
        ) : ''}

      </div>
    </Paper>
  }

  // Security deposit details
  function renderSecurityDepositDetails() {
    if (isPhone) {
      const cs = phoneCardStyle();
      return (
        <div style={cs.card}>
          <div style={cs.header}>Security Deposit</div>
          <div style={{ padding: '10px 14px 6px' }}>
            <GobikesField type={GbkFieldType.RADIO} editMode={editMode}
              value={currentBikeData.hubBike.needDeposit !== undefined ? currentBikeData.hubBike.needDeposit : (currentBikeData.hubBike.deposit > 0 ? 1 : 0)}
              updateFn={(v: number) => updateCurrentHubBikeData({ needDeposit: Number(v) })}
              selectOptions={[{ value: 0, display: 'No deposit needed' }, { value: 1, display: 'Deposit required' }]}
              gbkFieldSx={{ width: '100%' }} />
          </div>
          {Boolean(currentBikeData.hubBike.needDeposit) && (
            <div style={cs.field}>
              <div style={cs.label}>Deposit Amount</div>
              <GobikesField type={GbkFieldType.NUMBER} editMode={editMode}
                value={currentBikeData.hubBike.deposit}
                updateFn={(v: number) => updateCurrentHubBikeData({ deposit: v })}
                inputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                displayPrefix='₹' gbkFieldSx={{ width: '100%' }} />
            </div>
          )}
        </div>
      );
    }
    return <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
      <div className="header">
        <Typography className="header-left" fontSize={18} fontWeight={500}>Security Deposit</Typography>
      </div>
      <div className="content" >
        <div className="gbk-col" style={{ minWidth: 350 }}>
          <GobikesField
            type={GbkFieldType.RADIO}
            editMode={editMode}
            value={currentBikeData.hubBike.needDeposit !== undefined ? currentBikeData.hubBike.needDeposit : (currentBikeData.hubBike.deposit > 0 ? 1 : 0)}
            updateFn={(v: number) => {
              updateCurrentHubBikeData({ needDeposit: Number(v) })
            }}
            selectOptions={[
              { value: 0, display: 'I don\'t need a deposit for this vehicle' },
              { value: 1, display: 'I need a deposit for this vehicle' }
            ]}
          />
          <br />
        </div>
        <div className="gbk-col" style={{ minWidth: 300 }}>
          <GobikesField
            type={GbkFieldType.NUMBER}
            editMode={editMode}
            value={currentBikeData.hubBike.deposit}
            updateFn={(v: number) => {
              updateCurrentHubBikeData({ deposit: v })
            }}
            inputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            disabled={!currentBikeData.hubBike.needDeposit}
            displayPrefix='₹'
          />
          <br />
        </div>
      </div>
    </Paper>
  }

  // Terms and Conditions
  function renderTncDetails() {
    if (isPhone) {
      const cs = phoneCardStyle();
      return (
        <div style={cs.card}>
          <div style={cs.header}>Vehicle T&amp;C</div>
          <div style={{ padding: '10px 14px 6px', borderTop: '1px solid #f2f2f2' }}>
            {!editMode ? (
              !currentBikeData.hubBike.tnc?.length ? (
                <div style={{ fontSize: 13, color: '#999', fontStyle: 'italic' }}>No terms and conditions specified</div>
              ) : (
                <ol style={{ margin: '0 0 0 16px', padding: 0 }}>
                  {currentBikeData.hubBike.tnc.map((a, i) => <li key={i} style={{ fontSize: 13, paddingBottom: 6 }}>{a}</li>)}
                </ol>
              )
            ) : (
              <Fragment>
                {!currentBikeData.hubBike.tnc?.length && (
                  <div style={{ fontSize: 13, color: '#999', fontStyle: 'italic', marginBottom: 10 }}>Tap "Add" to enter conditions</div>
                )}
                {(currentBikeData.hubBike.tnc || []).map((a, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 13, color: '#666', flexShrink: 0, marginTop: 10 }}>{i + 1}.</span>
                    <GobikesField type={GbkFieldType.STRING} editMode={true} value={a}
                      updateFn={(v) => { const t = [...currentBikeData.hubBike.tnc]; t[i] = v; updateCurrentHubBikeData({ tnc: t }); }}
                      gbkFieldSx={{ width: '100%', flex: 1 }} />
                    <span style={{ cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', marginTop: 10 }}
                      onClick={() => { const t = [...currentBikeData.hubBike.tnc]; t.splice(i, 1); updateCurrentHubBikeData({ tnc: t }); }}>
                      <Cancel htmlColor={Colors.failureRed} fontSize="small" />
                    </span>
                  </div>
                ))}
                <Button variant="contained" fullWidth onClick={() => updateCurrentHubBikeData({ tnc: [...(currentBikeData.hubBike.tnc || []), ''] })}
                  sx={{ textTransform: 'none', fontWeight: 700, mt: 1 }}>
                  <Add /> Add condition
                </Button>
              </Fragment>
            )}
          </div>
          <div style={{ padding: '10px 14px 12px', borderTop: '1px solid #f2f2f2' }}>
            <GobikesField type={GbkFieldType.SWITCH} label={'Outstation allowed?'} editMode={editMode}
              value={currentBikeData.hubBike.outstationAllowed || false}
              updateFn={(v: boolean) => updateCurrentHubBikeData({ outstationAllowed: v })}
              gbkFieldSx={{ width: '100%' }} />
          </div>
        </div>
      );
    }
    return <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
      <div className="header">
        <Typography className="header-left" fontSize={18} fontWeight={500}>Vehicle Specific T&C</Typography>
      </div>
      <div className="content" >
        {!editMode ? (
          <div className="gbk-col no-pad-bot" style={{ minWidth: 300 }}>
            {!currentBikeData.hubBike.tnc || !currentBikeData.hubBike.tnc.length ? (
              <Typography variant='caption' color={'gray'}><i>No terms and conditions specified</i></Typography>
            ) : (
              <ol className='gbk-list'>
                {currentBikeData.hubBike.tnc.map((a, i) => (
                  <li key={i} style={{ paddingBottom: 8 }}>{a}</li>
                ))}
              </ol>
            )}
          </div>
        ) : ( // Edit mode ON
          <div className="gbk-col" style={{ minWidth: 600 }}>
            <br />
            {!currentBikeData.hubBike.tnc || !currentBikeData.hubBike.tnc.length ? (
              <Fragment>
                <Typography variant='caption' color={'gray'}><i>Click "Add" to enter terms and conditions</i></Typography>
                <br />
                <br />
              </Fragment>
            ) : (
              <Fragment>
                {currentBikeData.hubBike.tnc.map((a, i) => (
                  <Fragment key={i} >
                    <Box className="gbk-col no-pad-bot" sx={{ minWidth: { xs: 250, md: 700 } }}>
                      <div key={`pre-${i}`} className='gbk-field-prefix'>{i + 1}.</div>
                      <GobikesField
                        key={`field-${i}`}
                        type={GbkFieldType.STRING}
                        editMode={editMode}
                        value={a}
                        updateFn={(v) => {
                          const newTnc = currentBikeData.hubBike.tnc;
                          newTnc[i] = v;
                          updateCurrentHubBikeData({ tnc: newTnc })
                        }}
                      />
                      <span
                        className="gbk-icon-span clickable"
                        style={{ color: Colors.failureRed, verticalAlign: 'bottom', lineHeight: '54px', marginLeft: '8px' }}
                        onClick={() => {
                          const newTnc = currentBikeData.hubBike.tnc;
                          newTnc.splice(i, 1);
                          updateCurrentHubBikeData({ tnc: newTnc });
                        }}
                      >
                        <Cancel />
                      </span>
                    </Box>
                    <br />
                  </Fragment>
                ))}
                <br />
              </Fragment>
            )}
            <Button
              variant='contained'
              onClick={() => {
                const newTnc = [...currentBikeData.hubBike.tnc];
                newTnc.push('');
                updateCurrentHubBikeData({ tnc: newTnc })
              }}
            >
              <Add />
              Add new condition
            </Button>
          </div>
        )}
        <div className="gbk-row" style={{ minWidth: 300 }}>
          <GobikesField
            type={GbkFieldType.SWITCH}
            label={'Outstation allowed?'}
            editMode={editMode}
            value={currentBikeData.hubBike.outstationAllowed || false}
            updateFn={(v: boolean) => {
              updateCurrentHubBikeData({ outstationAllowed: v })
            }}
          />
        </div>
      </div>
    </Paper>
  }

  // Photo uploads
  function renderPhotoUploadContent() {
    const photoSlots: { label: HubBikeUploadLabels; display: string }[] = [
      { label: HubBikeUploadLabels.ODOMETER, display: 'Odometer' },
      { label: HubBikeUploadLabels.FRONT, display: 'Front' },
      { label: HubBikeUploadLabels.LEFT, display: 'Left' },
      { label: HubBikeUploadLabels.RIGHT, display: 'Right' },
      { label: HubBikeUploadLabels.BACK, display: 'Back' },
      { label: HubBikeUploadLabels.TYRE, display: 'Tyre' },
      { label: HubBikeUploadLabels.ENGINE, display: 'Engine' },
    ];
    if (isPhone) {
      const cs = phoneCardStyle();
      return (
        <div style={cs.card} ref={photoSectionRef}>
          <div style={cs.header}>Photos</div>
          <div className="photo-slots" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, padding: '10px 14px 14px' }}>
            {photoSlots.map(({ label, display }) => {
              const upload = vehicleUploads[label];
              const hasApprovedImage = !!currentBikeData.hubBike?.imageData?.[label];
              let statusChip = null;
              if (upload) {
                if (upload.adminApproved === true) statusChip = <Chip label="✓" color="success" size="small" />;
                else if (upload.adminApproved === false) statusChip = <Chip label="✗" color="error" size="small" />;
                else statusChip = <Chip label="…" color="warning" size="small" />;
              } else if (hasApprovedImage) {
                statusChip = <Chip label="✓" color="success" size="small" />;
              }
              const isRejected = upload?.adminApproved === false;
              const displayUrl = photoDisplayValues[label] || upload?.publicUrl || '';
              return (
                <div key={label} style={{ flex: '1 1 calc(50% - 5px)', minWidth: 0 }}>
                  <div style={{ position: 'relative' }}>
                    {uploadingLabel === label ? (
                      <div style={{ height: 90, borderRadius: 10, background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CircularProgress size={24} />
                      </div>
                    ) : isRejected && !displayUrl ? (
                      <div style={{ height: 90, border: '2px dashed #d32f2f', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#d32f2f', fontSize: 11, textAlign: 'center', padding: 6 }}>
                        Rejected.<br />Re-upload.
                      </div>
                    ) : (
                      <GobikesField label={''} tag={label} type={GbkFieldType.UPLOAD} editMode={true}
                        value={displayUrl} updateFn={v => handleVehicleImageUpload(label, v)}
                        gbkFieldSx={{ width: '100%' }} />
                    )}
                    {statusChip && (
                      <div style={{ position: 'absolute', top: 5, right: 5 }}>
                        {statusChip}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: '#666', fontWeight: 600, marginTop: 5, textAlign: 'center' }}>
                    {display}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0} ref={photoSectionRef}>
      <div className="header">
        <Typography className="header-left" fontSize={18} fontWeight={500}>Photos</Typography>
      </div>
      <div className="content">
        {photoSlots.map(({ label, display }) => {
          const upload = vehicleUploads[label];
          const hasApprovedImage = !!currentBikeData.hubBike?.imageData?.[label];
          let statusChip = null;
          if (upload) {
            if (upload.adminApproved === true)
              statusChip = <Chip label="Approved" color="success" size="small" sx={{ ml: 1, verticalAlign: 'middle' }} />;
            else if (upload.adminApproved === false)
              statusChip = <Chip label="Rejected" color="error" size="small" sx={{ ml: 1, verticalAlign: 'middle' }} />;
            else
              statusChip = <Chip label="Pending" color="warning" size="small" sx={{ ml: 1, verticalAlign: 'middle' }} />;
          } else if (hasApprovedImage) {
            statusChip = <Chip label="Approved" color="success" size="small" sx={{ ml: 1, verticalAlign: 'middle' }} />;
          }
          const isRejected = upload?.adminApproved === false;
          const displayUrl = photoDisplayValues[label] || upload?.publicUrl || '';
          const fieldLabel = <span>{display}{statusChip}</span>;
          return (
            <div className="gbk-col" style={{ minWidth: 300 }} key={label}>
              {uploadingLabel === label
                ? <CircularProgress size={24} />
                : isRejected && !displayUrl
                  ? <>
                      <div style={{ fontSize: '0.8rem', color: 'rgba(0,0,0,0.6)', marginBottom: 6 }}>{display}{statusChip}</div>
                      <div style={{
                        width: 150, height: 100, border: '2px dashed #d32f2f', borderRadius: 8,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#d32f2f', fontSize: '0.75rem', textAlign: 'center', padding: 8
                      }}>
                        Image rejected.<br />Please re-upload.
                      </div>
                    </>
                  : <GobikesField
                      label={fieldLabel}
                      tag={label}
                      type={GbkFieldType.UPLOAD}
                      editMode={true}
                      value={displayUrl}
                      updateFn={v => handleVehicleImageUpload(label, v)}
                    />
              }
              <br />
            </div>
          );
        })}
      </div>
    </Paper>
  }

}
